/**
 * The clinician's screen calibration, and the screen geometry that depends on
 * it.
 *
 * Calibration is the CLINICIAN's, not a patient's — it exists so that degrees
 * mean something on the doctor's own monitor while he judges a dose. The
 * patient's real calibration is measured in Phase 4 and lives in the
 * `calibrations` table. These two must never be confused. See DECISIONS.md
 * 2026-08-21.
 *
 * It lived in the preview panel and was read from there only; the exercise library
 * kept its own unsaved copy, so the same monitor could be calibrated two ways
 * on two screens. One home for it, read by everything that needs it.
 */

/**
 * Screen calibration is Phase 4. Until it exists this is a hand-set stand-in so
 * degrees still mean something on screen. Roughly a 24-inch 1920-wide monitor
 * at about 60 cm — a typical consulting-room setup.
 */
export const DEFAULT_PX_PER_DEGREE = 38;

const PX_PER_DEGREE_KEY = 'pathways.clinicianPxPerDegree';

export function loadPxPerDegree(): number {
  const stored = window.localStorage.getItem(PX_PER_DEGREE_KEY);
  const value = Number(stored);
  return Number.isFinite(value) && value > 0 ? value : DEFAULT_PX_PER_DEGREE;
}

export function savePxPerDegree(value: number): void {
  window.localStorage.setItem(PX_PER_DEGREE_KEY, String(value));
}

/**
 * The screen's size in CSS pixels, or null when the browser will not say —
 * which happens in headless and non-composited contexts. Everything that uses
 * this must degrade to saying nothing rather than to guessing.
 */
export function screenSizeCss(): { width: number; height: number } | null {
  const width = window.screen?.width ?? 0;
  const height = window.screen?.height ?? 0;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return null;
  }
  return { width, height };
}

/**
 * How much to shrink the degrees-to-pixels conversion so that a canvas smaller
 * than the screen shows a faithful SCALE MODEL of the full-screen view.
 *
 * Without this, a windowed preview draws the same movement in the same number
 * of pixels as full screen — so it is a CROP, not a smaller version. The
 * exercise then clamps the amplitude down to whatever fits the small pane
 * (`fitAmplitude`), and the doctor judges a 15-degree prescription against a
 * sweep of perhaps 9 degrees, with nothing on screen saying so.
 *
 * Uniform on both axes and never above 1, so the aspect ratio is preserved and
 * a canvas at or above screen size is left exactly alone. At full screen this
 * returns 1 and nothing changes.
 *
 * NOTE what this does and does not buy: correct PROPORTIONS, not correct
 * visual angle. A scale model answers "does this movement look right on a
 * screen"; only full screen answers "is fifteen degrees right for this
 * patient".
 */
export function scaleModelFactor(canvasCssWidth: number, canvasCssHeight: number): number {
  const screen = screenSizeCss();
  if (!screen) return 1;
  if (canvasCssWidth <= 0 || canvasCssHeight <= 0) return 1;
  return Math.min(1, canvasCssWidth / screen.width, canvasCssHeight / screen.height);
}

/**
 * For a parameter measured as a distance from screen centre: the share of the
 * screen's width the full side-to-side excursion needs, as a fraction.
 *
 * Returns null when the screen size is unknown. Greater than 1 means the
 * movement does not fit and the target will leave the screen.
 */
export function screenWidthFraction(distanceFromCentrePx: number): number | null {
  const screen = screenSizeCss();
  if (!screen) return null;
  return (distanceFromCentrePx * 2) / screen.width;
}
